import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import AppTheme from '../templates/shared-theme/AppTheme';
import ColorModeIconDropdown from '../templates/shared-theme/ColorModeIconDropdown';
// import { SitemarkIcon } from '../templates/sign-in/components/CustomIcons';
import { APP_ICON_HEIGHT, APP_ICON_SRC, APP_ICON_WIDTH } from '../constants/appIcon';
import { setGuestMode } from '../api/auth/authStorage';
import SeoHead from '../components/SeoHead';

const WEB_APPLICATION_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Pomocnik Żeglarza',
  description:
    'Dziennik jachtowy, kalkulatory nawigacyjne, dekoder świateł i latarni oraz szkolenia żeglarskie w jednej aplikacji.',
  applicationCategory: 'Navigation',
  operatingSystem: 'Web',
};

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const PageContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100%',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

export default function LoginMenuPage() {
  const navigate = useNavigate();

  const handleContinueAsGuest = () => {
    setGuestMode();
    navigate('/home');
  };

  return (
    <AppTheme>
      <SeoHead
        title="Dziennik jachtowy i kalkulatory nawigacyjne"
        description="Pomocnik Żeglarza — dziennik jachtowy, kalkulatory nawigacyjne, dekoder świateł i latarni oraz szkolenia żeglarskie w jednej aplikacji."
        path="/login-menu"
        jsonLd={WEB_APPLICATION_JSON_LD}
      />
      <CssBaseline enableColorScheme />
      <PageContainer direction="column" sx={{ justifyContent: 'space-between' }}>
        <ColorModeIconDropdown sx={{ position: 'fixed', top: '1rem', right: '1rem' }} />
        <Card variant="outlined">
          {/* <SitemarkIcon /> */}
          <Box
            component="img"
            src={APP_ICON_SRC}
            alt="Pomocnik Żeglarza"
            width={APP_ICON_WIDTH}
            height={APP_ICON_HEIGHT}
            fetchPriority="high"
            decoding="async"
            sx={(theme) => ({ width: APP_ICON_WIDTH, height: APP_ICON_HEIGHT, alignSelf: 'center', filter: 'brightness(0)', ...theme.applyStyles('dark', { filter: 'brightness(0) invert(1)' }) })}
          />
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
          >
            Yacht Sailing Helper
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Dziennik, mapy, logi, kalkulatory — Twoje podręczne centrum dowodzenia
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
            >
              Zaloguj się
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/register')}
            >
              Zarejestruj się
            </Button>
            <Divider />
            <Button
              variant="text"
              onClick={handleContinueAsGuest}
              sx={{ color: 'text.secondary' }}
            >
              Kontynuuj jako gość
            </Button>
          </Box>
        </Card>
      </PageContainer>
    </AppTheme>
  );
}
